"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase";
import { GoogleSignInButton } from "@/app/components/GoogleSignInButton";
import { readGoogleAuthContext } from "@/lib/google-auth";
import { trackGA4Event } from "@/lib/analytics";
import { trackServerEvent } from "@/app/actions/analytics";
import { parseStoredCampaignAttribution } from "@/lib/campaign-attribution";
import { buildCreateProfilePayload } from "@/lib/account/profile-payload";
import {
  uploadPendingSignupAvatar,
  withoutPendingAvatar,
} from "@/lib/account/pending-avatar";
import { completeEmployerSignupFromSession } from "@/app/actions/employer-signup";
import { recordSignupMarketingConsent } from "@/app/actions/account-settings";
import type { SignupEntryIntent } from "@/lib/signup-next";
import {
  resolveExistingCandidateLoginDestination,
  resolveExistingEmployerLoginDestination,
  resolveSignupCompletionDestination,
  sanitiseOptionalSignupNext,
  sanitiseSignupSource,
} from "@/lib/signup-next";

const INPUT_CLASS =
  "px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg transition-all outline-none focus:border-gold focus:shadow-[0_0_0_3px_rgba(196,151,90,0.1)] focus:bg-white placeholder:text-[#B0A99F]";

const LAST_VISITED_AREA_KEY = "peakwave_last_visited_area";

async function hasActionableCandidateItem(
  supabase: ReturnType<typeof createClient>,
  profileId: string,
  userId: string,
): Promise<boolean> {
  const [{ data: conversations }, { count: pendingLinkCount }] =
    await Promise.all([
      supabase
        .from("conversations")
        .select("id, placement_status")
        .eq("candidate_id", profileId)
        .eq("status", "active")
        .limit(100),
      supabase
        .from("profile_links")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", userId)
        .eq("status", "pending"),
    ]);
  if (
    (conversations ?? []).some(
      (conversation) => conversation.placement_status === "requested",
    )
  ) {
    return true;
  }
  if ((pendingLinkCount ?? 0) > 0) return true;

  const conversationIds = (conversations ?? []).map(
    (conversation) => conversation.id,
  );
  if (conversationIds.length === 0) return false;
  const { count } = await supabase
    .from("messages")
    .select("id", { count: "exact", head: true })
    .in("conversation_id", conversationIds)
    .eq("sender_type", "employer")
    .is("read_at", null);
  return (count ?? 0) > 0;
}

export function LoginForm() {
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const safeSignupRedirect = sanitiseOptionalSignupNext(redirectTo);
  const emailConfirmed = searchParams.get("email_confirmed") === "true";
  const authFailed = searchParams.get("error") === "auth_failed";
  const googleFailed = searchParams.get("error") === "google_signin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);

    try {
      const supabase = createClient();
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      // Determine redirect based on user type
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (authUser) {
        const prefersEmployer =
          (authUser.user_metadata as { signup_role?: string } | undefined)
            ?.signup_role === "employer";

        const { data: profileRow } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", authUser.id)
          .maybeSingle();

        if (profileRow) {
          const actionable = await hasActionableCandidateItem(
            supabase,
            profileRow.id,
            authUser.id,
          );
          window.location.href = resolveExistingCandidateLoginDestination(
            redirectTo,
            {
              hasActionableItem: actionable,
              lastVisitedArea: localStorage.getItem(LAST_VISITED_AREA_KEY),
            },
          );
          return;
        }

        const { data: employerUserRow } = await supabase
          .from("employer_users")
          .select("id, company_id")
          .eq("user_id", authUser.id)
          .maybeSingle();

        if (employerUserRow) {
          // Employer - keep redirects on employer surfaces
          window.location.href = resolveExistingEmployerLoginDestination(redirectTo);
          return;
        }

        // Neither profile nor employer_users - check if this is a confirmed
        // candidate who needs their profile created from pending data (PKCE
        // cross-browser scenario: email confirmed but callback couldn't
        // create because code exchange failed).
        const { data: pendingCandidate, error: pendingErr } = await supabase
          .from("pending_signups")
          .select("id, signup_data")
          .eq("email", (authUser.email ?? "").toLowerCase())
          .eq("used", false)
          .maybeSingle();

        if (pendingErr) {
          console.error("[login] pending_signups lookup failed:", pendingErr.message);
        }

        // A metadata-employer must never have a candidate profile
        // auto-created from a stale pending_signups row.
        if (pendingCandidate?.signup_data && !prefersEmployer) {
          const d = pendingCandidate.signup_data as Record<string, unknown>;
          const campaignAttribution = parseStoredCampaignAttribution(
            d.campaignAttribution,
          );
          const pendingNext = sanitiseOptionalSignupNext(
            typeof d.next === "string" ? d.next : null,
          );
          const entryIntent: SignupEntryIntent =
            d.entryIntent === "companion" || d.entryIntent === "work"
              ? d.entryIntent
              : "generic";
          const pendingEntrySource = sanitiseSignupSource(d.entrySource);
          const profilePayload = buildCreateProfilePayload({
            userId: authUser.id,
            authEmail: authUser.email,
            signupData: d,
          });

          const { data: rpcResult, error: rpcErr } = await supabase.rpc("create_profile", {
            profile_data: profilePayload,
          });

          if (!rpcErr) {
            // Fallback: if deployed RPC doesn't handle availabilities, insert separately
            if (profilePayload.availabilities.length > 0) {
              let profileId: string | null = typeof rpcResult === "string" ? rpcResult : null;
              if (!profileId) {
                const { data: profileRow } = await supabase
                  .from("profiles")
                  .select("id")
                  .eq("user_id", authUser.id)
                  .single();
                profileId = profileRow?.id ?? null;
              }
              if (profileId) {
                const { count } = await supabase
                  .from("availability_windows")
                  .select("id", { count: "exact", head: true })
                  .eq("profile_id", profileId);
                if (count === 0) {
                  await supabase.from("availability_windows").insert(
                    profilePayload.availabilities.map((a) => ({
                      profile_id: profileId,
                      season_label: a.season_label,
                      available_from: a.available_from,
                      available_to: a.available_to,
                      dates_flexible: a.dates_flexible,
                      flexible_earliest_start: a.flexible_earliest_start,
                      flexible_latest_end: a.flexible_latest_end,
                      positions: a.positions,
                      preferred_locations: a.preferred_locations,
                      status: "actively_looking" as const,
                    }))
                  );
                }
              }
            }

            const pendingAvatar = await uploadPendingSignupAvatar({
              supabase,
              userId: authUser.id,
              value: d.photoDataUrl,
            });
            if (pendingAvatar.error) {
              console.error(
                "[login] Pending avatar upload failed:",
                pendingAvatar.error,
              );
            }
            await supabase
              .from("pending_signups")
              .update({
                signup_data: withoutPendingAvatar(d),
                used: true,
              })
              .eq("id", pendingCandidate.id);
            if (d.marketingUpdates === true) {
              try {
                await recordSignupMarketingConsent();
              } catch {
                // Non-blocking: the choice can be made in Settings.
              }
            }
            await trackServerEvent({
              event: "signup_completed",
              properties: {
                has_referral: Boolean(d.refCode),
                has_availability_windows:
                  profilePayload.availabilities.length > 0,
                signup_method: "login_form",
                entry_intent: entryIntent,
                ...(pendingEntrySource ? { entry_source: pendingEntrySource } : {}),
                ...(campaignAttribution ?? {}),
              },
            });

            trackGA4Event("sign_up", { method: "candidate" });
            window.location.href = resolveSignupCompletionDestination({
              hasAvailabilityWindows:
                profilePayload.availabilities.length > 0,
              next: pendingNext,
              loginRedirect: safeSignupRedirect,
              entryIntent,
            });
            return;
          }

          // RPC errored. If a profile already exists (concurrent creation by
          // the auth callback), treat as success rather than bouncing the user
          // back to /signup as if their signup was lost.
          const { data: existingProfile } = await supabase
            .from("profiles")
            .select("id")
            .eq("user_id", authUser.id)
            .maybeSingle();
          if (existingProfile) {
            const pendingAvatar = await uploadPendingSignupAvatar({
              supabase,
              userId: authUser.id,
              value: d.photoDataUrl,
            });
            if (pendingAvatar.error) {
              console.error(
                "[login] Pending avatar upload failed:",
                pendingAvatar.error,
              );
            }
            await supabase
              .from("pending_signups")
              .update({
                signup_data: withoutPendingAvatar(d),
                used: true,
              })
              .eq("id", pendingCandidate.id);
            if (d.marketingUpdates === true) {
              try {
                await recordSignupMarketingConsent();
              } catch {
                // Non-blocking: the choice can be made in Settings.
              }
            }
            window.location.href = resolveSignupCompletionDestination({
              hasAvailabilityWindows:
                profilePayload.availabilities.length > 0,
              next: pendingNext,
              loginRedirect: safeSignupRedirect,
              entryIntent,
            });
            return;
          }
        }

        // Use the same verified, atomic completion as the email callback.
        // Current metadata takes priority over stale signup staging.
        const { created: employerCreated } = await completeEmployerSignupFromSession();
        if (employerCreated) {
          trackGA4Event("sign_up", { method: "employer" });
          trackGA4Event("generate_lead", { method: "employer" });
          window.location.href = "/employer/welcome";
          return;
        }
        if (prefersEmployer) {
          window.location.href = "/employer-signup";
          return;
        }

        // No pending data - send to signup in complete_profile mode so
        // the form uses the existing auth session instead of trying signUp()
        window.location.href = "/signup";
        return;
      }

      // Fallback - hard navigation to requested redirect
      window.location.href = resolveExistingCandidateLoginDestination(
        redirectTo,
        {
          lastVisitedArea: localStorage.getItem(LAST_VISITED_AREA_KEY),
        },
      );
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError("Enter your email above, then click forgot password.");
      return;
    }
    setResetting(true);
    setError(null);
    try {
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email,
        { redirectTo: `${window.location.origin}/auth/callback?next=/companion` }
      );
      if (resetError) {
        setError(resetError.message);
      } else {
        setResetSent(true);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="max-w-[440px] mx-auto px-5 pb-20">
      <div className="text-center mb-10">
        <h1 className="font-serif text-[36px] mb-2">Welcome back</h1>
        <p className="text-[15px] text-text-muted">
          Log in to your PeakWave account.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-[22px] p-7 md:p-11 border border-border-light shadow-[0_4px_32px_rgba(0,0,0,0.04)]"
      >
        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            {error}
          </div>
        )}

        {googleFailed && (
          <div role="alert" className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            Google sign-in was cancelled or could not be completed. Please try again.
          </div>
        )}

        {authFailed && (
          <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            Your sign-in link has expired or is no longer valid. Please{" "}
            <a href="/join" className="underline font-semibold">
              sign up again
            </a>{" "}
            to receive a new one, or log in below if you already have a password.
          </div>
        )}

        {emailConfirmed && (
          <div className="bg-success/10 border border-success/30 text-success rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            Email confirmed! Log in below to get started.
          </div>
        )}

        {resetSent && (
          <div className="bg-success/10 border border-success/30 text-success rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            Password reset link sent. Check your inbox.
          </div>
        )}

        <GoogleSignInButton {...readGoogleAuthContext(searchParams)} next={redirectTo} disabled={loading} />

        <div className="flex flex-col mb-5">
          <label
            htmlFor="login-email"
            className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2"
          >
            Email
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT_CLASS}
          />
        </div>

        <div className="flex flex-col mb-2">
          <label
            htmlFor="login-password"
            className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${INPUT_CLASS} w-full pr-11`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-charcoal transition-colors bg-transparent border-none cursor-pointer p-1"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="text-right mb-6">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetting}
            className="text-[13px] text-gold font-medium bg-transparent border-none cursor-pointer font-sans hover:text-gold-light transition-colors p-0"
          >
            {resetting ? "Sending..." : "Forgot your password?"}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-navy text-white rounded-[10px] text-[15px] font-bold border-none cursor-pointer font-sans transition-all hover:bg-gold hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(196,151,90,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Logging in..." : "Log In \u2192"}
        </button>

        <p className="text-center text-[13px] text-text-muted mt-5">
          Don&apos;t have an account?{" "}
          <Link href="/join" className="text-gold font-semibold">
            Sign up
          </Link>
        </p>
      </form>
    </div>
  );
}
